import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from '@/components/Layout';

import EmployeesPage from '@/pages/parametrizacion/employees/EmployeesPage';

import LoginPage from '@/pages/LoginPage';
import UnauthorizedPage from '@/pages/UnauthorizedPage';
import UsuariosPage from '@/pages/parametrizacion/usuarios/UsuariosPage';
import ShiftManagementPage from '@/pages/shiftManagement/ShiftManagementPage';
import WeeklyOverviewPage from '@/pages/shiftManagement/WeeklyOverviewPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import ChangePasswordPage from '@/pages/profile/ChangePasswordPage';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider, useAuth, ProtectedRoute, MODULES } from '@/contexts/AuthContext';
import EmployeeDocumentsPage from '@/pages/documents/EmployeeDocumentsPage';
import LaundryCenterDocumentsPage from '@/pages/documents/LaundryCenterDocumentsPage';
import DocumentationHomePage from '@/pages/documents/DocumentationHomePage';
import EmployeesDocsHome from '@/pages/documents/EmployeesDocsHome';
import CentersDocsHome from '@/pages/documents/CentersDocsHome';
import HospedajesDocsHome from '@/pages/documents/HospedajesDocsHome';
import HospedajeDocumentsPage from '@/pages/documents/HospedajeDocumentsPage';
import DocumentTypesPage from '@/pages/parametrizacion/documentTypes/DocumentTypesPage';
import EdificiosPage from '@/pages/parametrizacion/edificios/EdificiosPage';
import HospedajesPage from '@/pages/parametrizacion/hospedajes/HospedajesPage';
import GarmentTypesPage from '@/pages/parametrizacion/garmentTypes/GarmentTypesPage';
import CamasPage from '@/pages/parametrizacion/camas/CamasPage';
import BanosPage from '@/pages/parametrizacion/banos/BanosPage';
import ZonasPage from '@/pages/parametrizacion/zonas/ZonasPage';
import PropertiesPage from '@/pages/PropertiesPage.jsx';
import HospedajeDashboard from '@/pages/HospedajeDashboard';
import InventarioHabitacion from '@/pages/InventarioHabitacion';
import SolicitarLenceriaLimpia from '@/pages/SolicitarLenceriaLimpia';
import InventarioLenceriaPage from '@/pages/inventario/lenceria/InventarioLenceriaPage';
import ProvidersPage from '@/pages/parametrizacion/providers/ProvidersPage';
import ConsumablesPage from '@/pages/parametrizacion/consumables/ConsumablesPage';
import LavanderiaDashboard from '@/pages/lavanderia/LavanderiaDashboard';
import RecepcionRopaPage from '@/pages/lavanderia/lenceria/RecepcionRopaPage';
import InventarioInsumosPage from '@/pages/lavanderia/insumos/InventarioInsumosPage';
import EntregaInsumosPage from '@/pages/lavanderia/insumos/EntregaInsumosPage';
import InventarioLenceriaGlobalPage from '@/pages/lavanderia/lenceria/InventarioLenceriaPage';
import CortesInventarioPage from '@/pages/lavanderia/cortesInventario/CortesInventarioPage';
import CorteInventarioDetailPage from '@/pages/lavanderia/cortesInventario/CorteInventarioDetailPage';
import SolicitudLenceriaPage from '@/pages/solicitudes/SolicitudLenceriaPage';
import SolicitudInsumosPage from '@/pages/solicitudes/SolicitudInsumosPage';
import SolicitudLenceriaListPage from '@/pages/solicitudes/SolicitudLenceriaListPage';
import SolicitudInsumosListPage from '@/pages/solicitudes/SolicitudInsumosListPage';
import SolicitudesDelDiaPage from '@/pages/solicitudes/SolicitudesDelDiaPage';
import RutaPage from '@/pages/ruta/RutaPage';
import ExtraRutasPage from '@/pages/ruta/ExtraRutasPage';
import InventarioVerticalPage from '@/pages/lavanderia/inventario-vertical/InventarioVerticalPage';
import EdificioInventarioDetail from '@/pages/lavanderia/inventario-vertical/EdificioInventarioDetail';
import RopaEnDesmanchePage from '@/pages/lavanderia/RopaEnDesmanchePage';
import SolicitudesDesarrolloPage from '@/pages/solicitudes-desarrollo/SolicitudesDesarrolloPage';
import ReporteDashboard from '@/pages/ReporteDashboard.jsx';
import ReporteEntregasHorizontal from '@/pages/reportes/ReporteEntregasHorizontal.jsx';
import ReporteEntregasVertical from '@/pages/reportes/ReporteEntregasVertical.jsx';
import PQRsPage from '@/pages/PQRsPage';

// Módulo de Asistencia
import AsistenciaPage from '@/pages/asistencia/AsistenciaPage';

// Utilidad: detectar si el usuario es operario/operador
const isOperarioUser = (user) => {
  if (!user) return false;
  // roles en array
  if (Array.isArray(user.roles)) {
    const roles = user.roles.map((r) => String(r || '').toLowerCase().trim());
    if (roles.includes('operario') || roles.includes('operador')) return true;
  }
  // rol/role en string
  const primary = String(user.rol ?? user.role ?? '').toLowerCase().trim();
  return primary === 'operario' || primary === 'operador';
};

// Componente para rutas públicas que redirigen si el usuario ya está autenticado
const PublicRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return children;
  const isOperario = isOperarioUser(user);
  const isConductor = user?.role === 'conductor';
  if (isConductor) return <Navigate to="/ruta" replace />;
  return <Navigate to={isOperario ? '/operario-dashboard' : '/dashboard'} replace />;
};

// Landing por defecto según rol
const DefaultLanding = () => {
  const { user } = useAuth();
  const isOperario = isOperarioUser(user);
  const isConductor = user?.role === 'conductor';
  if (isConductor) return <Navigate to="/ruta" replace />;
  return <Navigate to={isOperario ? '/operario-dashboard' : '/dashboard'} replace />;
};

function AppContent() {
  return (
    <Layout>
      <Routes>
        {/* Rutas públicas */}
        <Route path="/login" element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } />
        <Route path="/forgot-password" element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        } />
        <Route path="/reset-password" element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        } />
        
        {/* Ruta raíz redirige según rol si autenticado, sino a login */}
        <Route path="/" element={
          <ProtectedRoute>
            <DefaultLanding />
          </ProtectedRoute>
        } />

        {/* Rutas protegidas */}
        <Route path="/dashboard" element={
          <ProtectedRoute requiredModule={MODULES.DASHBOARD}>
             <ReporteDashboard />
          </ProtectedRoute>
        } />
          
        {/* Reporte de Entregas Horizontal */}
        <Route path="/reportes/entregas-horizontal" element={
          <ProtectedRoute requiredModule={MODULES.REPORTS}>
            <ReporteEntregasHorizontal />
          </ProtectedRoute>
        } />

        {/* Reporte de Entregas Vertical */}
        <Route path="/reportes/entregas-vertical" element={
          <ProtectedRoute requiredModule={MODULES.REPORTS}>
            <ReporteEntregasVertical />
          </ProtectedRoute>
        } />
        
        {/* Propiedades */}
        <Route path="/propiedades" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <PropertiesPage />
          </ProtectedRoute>
        } />
        
        {/* Hospedajes de un edificio específico */}
        <Route path="/propiedades/hospedajes/:edificioId" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <PropertiesPage />
          </ProtectedRoute>
        } />
        
        {/* Dashboard de Hospedaje */}
        <Route path="/hospedaje/:hospedajeId" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <HospedajeDashboard />
          </ProtectedRoute>
        } />
        
        {/* Inventario de Lencería */}
        <Route path="/hospedaje/:hospedajeId/inventario-lenceria" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <InventarioLenceriaPage />
          </ProtectedRoute>
        } />
        
        {/* Inventario de Habitación */}
        <Route path="/hospedaje/:hospedajeId/inventario-habitacion" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <InventarioHabitacion />
          </ProtectedRoute>
        } />
        
        {/* Solicitar Lencería Limpia */}
        <Route path="/hospedaje/:hospedajeId/solicitar-lenceria" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <SolicitarLenceriaLimpia />
          </ProtectedRoute>
        } />
        
        {/* Solicitudes */}
        <Route path="/solicitudes/lenceria" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <SolicitudLenceriaPage />
          </ProtectedRoute>
        } />
        <Route path="/solicitudes/lenceria/:edificioId" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <SolicitudLenceriaPage />
          </ProtectedRoute>
        } />
        <Route path="/solicitudes/insumos" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <SolicitudInsumosPage />
          </ProtectedRoute>
        } />
        <Route path="/solicitudes/insumos/:edificioId" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <SolicitudInsumosPage />
          </ProtectedRoute>
        } />
        <Route path="/solicitudes/lenceria/lista" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <SolicitudLenceriaListPage />
          </ProtectedRoute>
        } />
        <Route path="/solicitudes/insumos/lista" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <SolicitudInsumosListPage />
          </ProtectedRoute>
        } />
        

        
        {/* Ruta - Admin y Conductor */}
        <Route path="/ruta" element={
          <ProtectedRoute requiredModule={MODULES.RUTA_ADMIN}>
            <RutaPage />
          </ProtectedRoute>
        } />
        
        {/* Extra Rutas - Solicitudes no aprobadas */}
        <Route path="/extra-rutas" element={
          <ProtectedRoute requiredModule={MODULES.RUTA_ADMIN}>
            <ExtraRutasPage />
          </ProtectedRoute>
        } />
        
        {/* Solicitudes del Día */}
        <Route path="/solicitudes-del-dia" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <SolicitudesDelDiaPage />
          </ProtectedRoute>
        } />
        
        {/* Solicitudes de Desarrollo */}
        <Route path="/solicitudes-desarrollo" element={
          <ProtectedRoute requiredModule={MODULES.SOLICITUDES}>
            <SolicitudesDesarrolloPage />
          </ProtectedRoute>
        } />
        
        {/* PQRs - Peticiones, Quejas y Reclamos */}
        <Route path="/pqrs" element={
          <ProtectedRoute requiredModule={MODULES.PQRS}>
            <PQRsPage />
          </ProtectedRoute>
        } />

        {/* Asistencia de Personal (tablero único con modales) */}
        <Route path="/asistencia" element={
          <ProtectedRoute requiredModule={MODULES.ATTENDANCE}>
            <AsistenciaPage />
          </ProtectedRoute>
        } />

        
        <Route path="/parametrizacion/empleados" element={
          <ProtectedRoute requiredModule={MODULES.EMPLOYEES}>
            <EmployeesPage />
          </ProtectedRoute>
        } />
        
        <Route path="/parametrizacion/tipos-archivo" element={
          <ProtectedRoute requiredModule={MODULES.DOCUMENT_TYPES}>
            <DocumentTypesPage />
          </ProtectedRoute>
        } />
        
        <Route path="/parametrizacion/edificios" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS_ADMIN}>
            <EdificiosPage />
          </ProtectedRoute>
        } />
        
        <Route path="/parametrizacion/zonas" element={
          <ProtectedRoute requiredModule={MODULES.ZONAS}>
            <ZonasPage />
          </ProtectedRoute>
        } />
        
        <Route path="/parametrizacion/hospedajes" element={
          <ProtectedRoute requiredModule={MODULES.HOSPEDAJES_ADMIN}>
            <HospedajesPage />
          </ProtectedRoute>
        } />
        
        <Route path="/parametrizacion/tipos-prenda" element={
          <ProtectedRoute requiredModule={MODULES.GARMENT_TYPES}>
            <GarmentTypesPage />
          </ProtectedRoute>
        } />
        
        <Route path="/parametrizacion/camas" element={
          <ProtectedRoute requiredModule={MODULES.CAMAS}>
            <CamasPage />
          </ProtectedRoute>
        } />
        
        <Route path="/parametrizacion/banos" element={
          <ProtectedRoute requiredModule={MODULES.CAMAS}>
            <BanosPage />
          </ProtectedRoute>
        } />
                
        <Route path="/parametrizacion/usuarios" element={
          <ProtectedRoute requiredModule={MODULES.USERS}>
            <UsuariosPage />
          </ProtectedRoute>
        } />
        
        {/* Proveedores */}
        <Route path="/parametrizacion/proveedor" element={
          <ProtectedRoute requiredModule={MODULES.PROVEEDOR}>
            <ProvidersPage />
          </ProtectedRoute>
        } />
        
        {/* Consumibles */}
        <Route path="/parametrizacion/insumos" element={
          <ProtectedRoute requiredModule={MODULES.INSUMOS}>
            <ConsumablesPage />
          </ProtectedRoute>
        } />
        
        {/* Lavandería */}
        <Route path="/lavanderia" element={
          <ProtectedRoute requiredModule={MODULES.LAUNDRY}>
            <LavanderiaDashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/lavanderia/recepcion" element={
          <ProtectedRoute requiredModule={MODULES.LAUNDRY_RECEPTION}>
            <RecepcionRopaPage />
          </ProtectedRoute>
        } />
        
        <Route path="/lavanderia/entrega-insumos" element={
          <ProtectedRoute requiredModule={MODULES.LAUNDRY_SUPPLIES}>
            <EntregaInsumosPage />
          </ProtectedRoute>
        } />
        
        <Route path="/lavanderia/inventario-insumos" element={
          <ProtectedRoute requiredModule={MODULES.LAUNDRY_SUPPLIES}>
            <InventarioInsumosPage />
          </ProtectedRoute>
        } />
        
        <Route path="/lavanderia/inventario" element={
          <ProtectedRoute requiredModule={MODULES.LAUNDRY_INVENTORY}>
            <InventarioLenceriaGlobalPage />
          </ProtectedRoute>
        } />
        
        <Route path="/lavanderia/cortes-inventario" element={
          <ProtectedRoute requiredModule={MODULES.LAUNDRY_INVENTORY}>
            <CortesInventarioPage />
          </ProtectedRoute>
        } />

        <Route path="/lavanderia/cortes-inventario/:corteId" element={
          <ProtectedRoute requiredModule={MODULES.LAUNDRY_INVENTORY}>
            <CorteInventarioDetailPage />
          </ProtectedRoute>
        } />

        <Route path="/lavanderia/inventario-vertical" element={
          <ProtectedRoute requiredModule={MODULES.LAUNDRY_INVENTORY}>
            <InventarioVerticalPage />
          </ProtectedRoute>
        } />
        
        <Route path="/lavanderia/inventario-vertical/:edificioId" element={
          <ProtectedRoute requiredModule={MODULES.LAUNDRY_INVENTORY}>
            <EdificioInventarioDetail />
          </ProtectedRoute>
        } />
        
        <Route path="/lavanderia/ropa-desmanche" element={
          <ProtectedRoute requiredModule={MODULES.LAUNDRY_INVENTORY}>
            <RopaEnDesmanchePage />
          </ProtectedRoute>
        } />
        
        {/* Documentos */}
        <Route path="/documentacion" element={
          <ProtectedRoute requiredModule={MODULES.DOCUMENTATION}>
            <DocumentationHomePage />
          </ProtectedRoute>
        } />
        <Route path="/documentacion/empleados" element={
          <ProtectedRoute requiredModule={MODULES.DOCUMENTATION}>
            <EmployeesDocsHome />
          </ProtectedRoute>
        } />
        <Route path="/documentacion/centros" element={
          <ProtectedRoute requiredModule={MODULES.DOCUMENTATION}>
            <CentersDocsHome />
          </ProtectedRoute>
        } />
        <Route path="/documentacion/empleados/:idEmpleado" element={
          <ProtectedRoute requiredModule={MODULES.EMPLOYEES}>
            <EmployeeDocumentsPage />
          </ProtectedRoute>
        } />
        <Route path="/documentacion/centros/:idLavanderia" element={
          <ProtectedRoute requiredModule={MODULES.WORKPLACES}>
            <LaundryCenterDocumentsPage />
          </ProtectedRoute>
        } />
        <Route path="/documentacion/hospedajes" element={
          <ProtectedRoute requiredModule={MODULES.DOCUMENTATION}>
            <HospedajesDocsHome />
          </ProtectedRoute>
        } />
        <Route path="/documentacion/hospedajes/:idHospedaje" element={
          <ProtectedRoute requiredModule={MODULES.EDIFICIOS}>
            <HospedajeDocumentsPage />
          </ProtectedRoute>
        } />
        <Route path="/parametrizacion/empleados/:idEmpleado/documentos" element={
          <ProtectedRoute requiredModule={MODULES.EMPLOYEES}>
            <EmployeeDocumentsPage />
          </ProtectedRoute>
        } />
        <Route path="/centros-lavado/:idLavanderia/documentos" element={
          <ProtectedRoute requiredModule={MODULES.WORKPLACES}>
            <LaundryCenterDocumentsPage />
          </ProtectedRoute>
        } />
        {/* Alias para compatibilidad con tarjetas existentes */}
        <Route path="/centros-lavado/:idLavanderia/documentacion" element={
          <ProtectedRoute requiredModule={MODULES.WORKPLACES}>
            <LaundryCenterDocumentsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/centros-lavado/:idLavanderia/turnos" element={
          <ProtectedRoute requiredModule={MODULES.WORKPLACES}>
            <ShiftManagementPage />
          </ProtectedRoute>
        } />

        <Route path="/supervision/turnos" element={
          <ProtectedRoute requiredModule={MODULES.WORKPLACES}>
            <WeeklyOverviewPage />
          </ProtectedRoute>
        } />

        <Route path="/ruta" element={
          <ProtectedRoute requiredModule={MODULES.RUTA_ADMIN}>
            <RutaPage />
          </ProtectedRoute>
        } />

        
        {/* Perfil */}
        <Route path="/perfil/cambiar-contrasena" element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        } />
        
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        
        {/* Ruta por defecto para páginas no encontradas */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
       
          <AppContent />
          <Toaster />
    
      </AuthProvider>
    </Router>
  );
}

export default App;